import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { NarrativeBeat } from "@/api/types";
import { DialogueBox } from "./DialogueBox";
import { tokens } from "@/art/tokens";
import { AudioManager } from "@/engine/AudioManager";

interface NarrativeBeatPlayerProps {
    beats: NarrativeBeat[];
    /** 전체 beat 시퀀스가 끝났을 때. */
    onComplete?: () => void;
    autoAdvanceMs?: number;
    className?: string;
    theme?: "default" | "soul";
}

/**
 * NarrativeBeat 배열을 순차 재생. 유저가 각 beat 클릭(스킵) 또는 자동 진행.
 * beat 간 gap 은 tokens.timing.narrativeBeatGap.
 */
export function NarrativeBeatPlayer({ beats, onComplete, autoAdvanceMs, className, theme = "default" }: NarrativeBeatPlayerProps) {
    const [index, setIndex] = useState(0);
    const [beatDone, setBeatDone] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [hiddenIllustrations, setHiddenIllustrations] = useState<Record<string, true>>({});
    const [replayNonce, setReplayNonce] = useState(0);
    const [narrationBlocked, setNarrationBlocked] = useState(false);
    const [impactNonce, setImpactNonce] = useState(0);
    const mountedRef = useRef(false);
    const narrationAudioRef = useRef<HTMLAudioElement | null>(null);

    const goNext = useCallback(() => {
        if (index + 1 >= beats.length) {
            onComplete?.();
            return;
        }
        setIndex(i => i + 1);
        setBeatDone(false);
    }, [index, beats.length, onComplete]);

    const goPrev = useCallback(() => {
        if (index <= 0) return;
        setIndex(i => Math.max(0, i - 1));
        setBeatDone(true);
    }, [index]);

    // 2026-04-22 E22: 긴 컷신에서 플레이어가 빠져나올 수 있도록 "전체 건너뛰기" 를 추가.
    // onComplete 를 직접 호출해 시퀀스 종료 신호를 넘김 — 부모는 보상/결과 화면으로 진행.
    const skipAll = useCallback(() => {
        AudioManager.sfx(tokens.audio.sfx.pageTurn, 0.6);
        onComplete?.();
    }, [onComplete]);

    useEffect(() => {
        if (!beatDone || !autoAdvanceMs) return;
        const t = window.setTimeout(() => goNext(), autoAdvanceMs);
        return () => window.clearTimeout(t);
    }, [beatDone, goNext, autoAdvanceMs]);

    useEffect(() => {
        if (!beatDone) return;
        const handler = (e: KeyboardEvent) => {
            if (e.code === "Space" || e.code === "Enter") goNext();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [beatDone, goNext]);

    useEffect(() => {
        setHiddenIllustrations({});
        setHistoryOpen(false);
        setReplayNonce(0);
        setNarrationBlocked(false);
    }, [beats]);

    useEffect(() => {
        if (!mountedRef.current) {
            mountedRef.current = true;
            return;
        }
        setImpactNonce((nonce) => nonce + 1);
    }, [index]);

    // 🚨 2026-04-23 Hook Rules 수정: 이전에 `if (beats.length === 0) return null;` 을 여기 두고
    //   이후에 useMemo/useEffect/useCallback 이 여러 개 호출되어, beats 유무에 따라 hook count
    //   가 달라졌다 (React #310 → 결과창 먹통·전체 tree crash 주요 원인).
    //   수정: 안전한 current 폴백 + 모든 hook 은 조건 없이 호출. 최종 JSX 직전에만 early null.
    const hasBeats = beats.length > 0;
    const fallbackBeat = { type: "narration" as const, text: "", speaker: null, variant: "standard", illustrationSrc: null, illustrationAlt: null, mood: null, audioSrc: null, variantLabel: null, variantCopy: null } as unknown as NarrativeBeat;
    const current = hasBeats ? beats[index] : fallbackBeat;
    const previous = index > 0 && hasBeats ? beats[index - 1] : null;
    const variant = current.variant ?? "standard";
    const illustrationSrc = current.illustrationSrc ?? undefined;
    // 이벤트 컷신 이미지 렌더 전체 비노출 정책 (2026-04-22 결정) —
    // illustration 자산이 충분히 갖춰지기 전까지는 텍스트 + variant fallback 장식만 사용.
    // illustrationSrc 가 있더라도 화면에 그리지 않음 (404 소음도 제거).
    // 데이터 자체는 유지 — 나중에 자산 채워지면 이 상수만 false → true 로 뒤집으면 됨.
    const ILLUSTRATIONS_DISABLED = false;
    const illustrationVisible = !ILLUSTRATIONS_DISABLED
        && Boolean(illustrationSrc) && !hiddenIllustrations[illustrationSrc ?? ""];
    const illustrationBorder = theme === "soul" ? `${tokens.color.burgundy}88` : `${tokens.color.parchmentDim}44`;
    const illustrationBackground = theme === "soul"
        ? "linear-gradient(180deg, rgba(92,31,46,0.26), rgba(14,12,24,0.94))"
        : `${tokens.color.ink}E6`;
    const illustrationGlow = theme === "soul"
        ? `0 22px 54px ${tokens.color.ink}88, 0 0 28px ${tokens.color.burgundy}44`
        : `0 18px 48px ${tokens.color.ink}66`;
    const progressAccent = theme === "soul" ? tokens.color.ember : tokens.color.parchmentDim;
    const baseVariantMeta = resolveBeatVariantMeta(variant, theme);
    // 이벤트별 라벨/카피 override — scenarios-source.json 에서 같은 variant 라도 이벤트마다
    // 다른 배너 문구를 노출하고 싶을 때 사용. 예: peace-or-conquest 는 "결단 컷신", last-banner-hold
    // 는 "후방의 결단" 처럼 차등. 미지정 시 기본 메타 유지.
    const variantMeta = {
        ...baseVariantMeta,
        label: current.variantLabel ?? baseVariantMeta.label,
        copy: current.variantCopy ?? baseVariantMeta.copy,
    };
    const ambientProfile = useMemo(
        () => resolveCutsceneAmbientProfile(variant, theme, current.mood),
        [variant, theme, current.mood],
    );
    const illustrationHeight = variant === "memorial" ? 460 : variant === "briefing" ? 320 : 380;
    const illustrationFit = variant === "briefing" ? "contain" : "cover";
    const illustrationPosition = variant === "decision" ? "center top" : "center";
    const showVariantBanner = variant !== "standard";
    const speakerShift = current.type === "dialogue"
        && current.speaker
        && current.speaker !== previous?.speaker
        ? `${current.speaker}의 시선으로 장면이 옮겨갑니다.`
        : null;
    const seenBeats = beats.slice(0, index + 1);
    useEffect(() => {
        if (!mountedRef.current) return;
        AudioManager.sfx(tokens.audio.sfx.pageTurn, 0.9);
        const accentCue = resolveNarrativeAccentCue(variant, current.mood);
        if (!accentCue) return;
        const timer = window.setTimeout(() => {
            AudioManager.sfx(accentCue.src, accentCue.volume);
        }, accentCue.delayMs);
        return () => window.clearTimeout(timer);
    }, [index, variant, current.mood]);

    useEffect(() => {
        narrationAudioRef.current?.pause();
        narrationAudioRef.current = null;
        setNarrationBlocked(false);

        if (!current.audioSrc) return;
        AudioManager.unlock();
        const audio = new Audio(current.audioSrc);
        audio.volume = 0.88;
        narrationAudioRef.current = audio;
        void audio.play().catch((err) => {
            setNarrationBlocked(true);
            console.warn("[NarrativeBeatPlayer] narration play failed", current.audioSrc, err);
        });

        return () => {
            audio.pause();
            if (narrationAudioRef.current === audio) {
                narrationAudioRef.current = null;
            }
        };
    }, [current.audioSrc, replayNonce]);

    const jumpToBeat = useCallback((beatIndex: number) => {
        setIndex(beatIndex);
        setBeatDone(false);
        setReplayNonce((n) => n + 1);
        setImpactNonce((nonce) => nonce + 1);
        AudioManager.sfx(tokens.audio.sfx.pageTurn, 0.72);
    }, []);

    const replayNarration = useCallback(() => {
        if (!current.audioSrc) return;
        AudioManager.unlock();
        narrationAudioRef.current?.pause();
        const audio = new Audio(current.audioSrc);
        audio.volume = 0.88;
        narrationAudioRef.current = audio;
        setNarrationBlocked(false);
        void audio.play().catch((err) => {
            setNarrationBlocked(true);
            console.warn("[NarrativeBeatPlayer] narration replay failed", current.audioSrc, err);
        });
    }, [current.audioSrc]);
    const dialogueText = (
        <AnimatePresence mode="wait">
            <DialogueBox
                key={`${index}:${replayNonce}`}
                speaker={current.type === "dialogue" ? current.speaker : null}
                text={current.text}
                mood={current.mood}
                onComplete={() => setBeatDone(true)}
                tickMs={tokens.timing.dialogueTick}
            />
        </AnimatePresence>
    );

    // 모든 hook 호출 후 안전하게 null 반환.
    if (!hasBeats) return null;

    return (
        <div className={className} onClick={beatDone ? goNext : undefined}>
            <div
                className="pointer-events-none mb-m overflow-hidden rounded-l"
                style={{
                    border: `1px solid ${showVariantBanner ? variantMeta.border : `${tokens.color.parchmentDim}33`}`,
                    background: `linear-gradient(180deg, rgba(14,12,24,0.92), rgba(14,12,24,0.7))`,
                    boxShadow: `0 22px 56px ${tokens.color.ink}88, inset 0 0 64px ${variantMeta.glow}`,
                }}
                aria-hidden="true"
            >
                <div
                    className="flex items-center justify-between px-m py-s"
                    style={{
                        borderBottom: `1px solid ${variantMeta.border}`,
                        background: `linear-gradient(90deg, rgba(14,12,24,0.96), ${variantMeta.bannerBackground})`,
                    }}
                >
                    {/* 2026-04-23: accent dash + uppercase kicker — Spirytale 언어 통일. */}
                    <div
                        className="font-body flex items-center gap-[6px]"
                        style={{
                            color: tokens.color.parchment,
                            fontSize: 14,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            opacity: 0.96,
                            textShadow: `${tokens.color.ink} 0px 2px 8px, 0 0 12px ${variantMeta.accent}44`,
                        }}
                    >
                        <motion.span
                            aria-hidden
                            animate={{ boxShadow: [`0 0 4px ${variantMeta.accent}66`, `0 0 10px ${variantMeta.accent}FF`, `0 0 4px ${variantMeta.accent}66`] }}
                            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                            style={{
                                width: 12,
                                height: 1,
                                background: variantMeta.accent,
                                opacity: 0.8,
                            }}
                        />
                        장면 · {showVariantBanner ? variantMeta.label : "기록된 장면"}
                    </div>
                    <div
                        className="font-stat"
                        style={{
                            color: tokens.color.parchment,
                            fontSize: tokens.type.size.small,
                            letterSpacing: tokens.type.letterSpacing.wide,
                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                        }}
                    >
                        장면 {String(index + 1).padStart(2, "0")}
                    </div>
                </div>
            </div>
            {/*
             * decision variant: 이미지 유무와 무관하게 항상 2-column 구조 유지.
             * 이미지 있으면 좌측에 이미지, 없으면 좌측에 variant 톤의 placeholder frame.
             * 이렇게 해야 "결단 장면" 의 시각적 특별감이 이미지 제작 전에도 유지됨.
             */}
            {variant === "decision" ? (
                <div
                    className="mb-m grid gap-m"
                    style={{
                        gridTemplateColumns: "minmax(0, 1.05fr) minmax(280px, 0.95fr)",
                        alignItems: "stretch",
                    }}
                >
                    <div
                        className="overflow-hidden rounded-l flex items-center justify-center"
                        style={{
                            border: `1px solid ${variantMeta.border}`,
                            background: variantMeta.panelBackground,
                            boxShadow: `0 18px 48px ${variantMeta.glow}`,
                            minHeight: 360,
                        }}
                    >
                        {illustrationVisible && illustrationSrc ? (
                            <NarrativeIllustrationStage
                                src={illustrationSrc}
                                alt={current.illustrationAlt ?? current.text}
                                onError={() => {
                                    if (illustrationSrc) {
                                        setHiddenIllustrations(prev => ({ ...prev, [illustrationSrc]: true }));
                                    }
                                }}
                                fit="cover"
                                position={illustrationPosition}
                                minHeight={360}
                                profile={ambientProfile}
                                impactNonce={impactNonce}
                            />
                        ) : (
                            <DecisionFallbackOrnament
                                accent={variantMeta.accent}
                                border={variantMeta.border}
                                profile={ambientProfile}
                                impactNonce={impactNonce}
                            />
                        )}
                    </div>
                    <div
                        className="rounded-l p-m"
                        style={{
                            border: `1px solid ${variantMeta.border}`,
                            background: variantMeta.panelBackground,
                            boxShadow: `0 18px 48px ${variantMeta.glow}`,
                        }}
                    >
                        <div
                            className="font-display mb-s"
                            style={{
                                color: variantMeta.accent,
                                fontSize: tokens.type.size.small,
                                letterSpacing: tokens.type.letterSpacing.wide,
                            }}
                        >
                            {variantMeta.label}
                        </div>
                        {dialogueText}
                    </div>
                </div>
            ) : illustrationVisible && illustrationSrc ? (
                <div
                    className="mb-m overflow-hidden rounded-l"
                    style={{
                        border: `1px solid ${showVariantBanner ? variantMeta.border : illustrationBorder}`,
                        background: showVariantBanner ? variantMeta.panelBackground : illustrationBackground,
                        boxShadow: showVariantBanner ? `0 18px 48px ${variantMeta.glow}` : illustrationGlow,
                    }}
                >
                    {theme === "soul" && (
                        <div
                            className="px-s py-xs font-display"
                            style={{
                                color: progressAccent,
                                fontSize: tokens.type.size.tiny,
                                letterSpacing: tokens.type.letterSpacing.wide,
                                borderBottom: `1px solid ${tokens.color.burgundy}44`,
                                background: "linear-gradient(90deg, rgba(92,31,46,0.42), rgba(14,12,24,0.12))",
                            }}
                        >
                            영혼의 잔향
                        </div>
                    )}
                    <NarrativeIllustrationStage
                        src={illustrationSrc}
                        alt={current.illustrationAlt ?? current.text}
                        onError={() => {
                            if (illustrationSrc) {
                                setHiddenIllustrations(prev => ({ ...prev, [illustrationSrc]: true }));
                            }
                        }}
                        maxHeight={illustrationHeight}
                        fit={illustrationFit}
                        position={illustrationPosition}
                        profile={ambientProfile}
                        impactNonce={impactNonce}
                    />
                </div>
            ) : variant === "memorial" ? (
                /*
                 * memorial variant + 이미지 없음: 460px 공백을 그대로 두면 "그냥 텍스트" 로
                 * 읽혀 추모 컷신의 무게가 빠짐. 대신 얇은 burgundy 프레임 + 중앙 장식으로
                 * 밤·불·추모 뉘앙스를 최소한으로 유지.
                 */
                <MemorialFallbackOrnament
                    accent={variantMeta.accent}
                    border={variantMeta.border}
                    panelBackground={variantMeta.panelBackground}
                    glow={variantMeta.glow}
                    profile={ambientProfile}
                    impactNonce={impactNonce}
                />
            ) : null}
            {variant !== "decision" && dialogueText}
            {speakerShift && (
                <div
                    className="mb-m rounded-pill px-m py-s font-body"
                    style={{
                        color: variantMeta.accent,
                        background: `${variantMeta.accent}12`,
                        border: `1px solid ${variantMeta.border}`,
                        fontSize: tokens.type.size.tiny,
                        letterSpacing: tokens.type.letterSpacing.wide,
                        textAlign: "center",
                    }}
                >
                    {speakerShift}
                </div>
            )}
            {historyOpen && (
                <div
                    className="relative mb-m overflow-hidden rounded-l p-m"
                    style={{
                        border: `1px solid ${variantMeta.border}`,
                        background: `linear-gradient(180deg, rgba(14,12,24,0.9), rgba(14,12,24,0.74))`,
                        boxShadow: `0 14px 36px ${tokens.color.ink}55`,
                    }}
                >
                    <img
                        src="/art/illustrations/event-storyboard-aurora.png"
                        alt=""
                        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                        style={{
                            opacity: 0.2,
                            objectPosition: "center center",
                            mixBlendMode: "screen",
                        }}
                        onError={(event) => {
                            (event.currentTarget as HTMLImageElement).style.display = "none";
                        }}
                    />
                    <div
                        className="pointer-events-none absolute inset-0"
                        style={{
                            background: "linear-gradient(180deg, rgba(14,12,24,0.58) 0%, rgba(14,12,24,0.86) 100%)",
                        }}
                    />
                    <div className="relative">
                        <div
                            className="mb-s font-display"
                            style={{
                                color: variantMeta.accent,
                                fontSize: tokens.type.size.tiny,
                                letterSpacing: tokens.type.letterSpacing.wide,
                            }}
                        >
                            장면 기록
                        </div>
                        <div className="grid gap-s">
                            {seenBeats.map((beat, beatIndex) => (
                                <button
                                    key={`history-${beatIndex}`}
                                    type="button"
                                    onClick={() => jumpToBeat(beatIndex)}
                                    className="w-full rounded-m px-s py-s text-left"
                                    style={{
                                        background: beatIndex === index ? `${variantMeta.accent}12` : `${tokens.color.parchment}06`,
                                        border: `1px solid ${beatIndex === index ? variantMeta.border : `${tokens.color.parchmentDim}1E`}`,
                                        backdropFilter: "blur(2px)",
                                        boxShadow: beatIndex === index ? `0 14px 28px ${variantMeta.glow}` : "none",
                                        transform: beatIndex === index ? "translateY(-1px)" : "none",
                                        cursor: "pointer",
                                    }}
                                >
                                    <div className="flex items-center justify-between gap-s">
                        <div
                            className="font-display"
                            style={{
                                color: tokens.color.parchment,
                                fontSize: 13.5,
                                letterSpacing: tokens.type.letterSpacing.wide,
                                textShadow: `${tokens.color.ink} 0px 2px 8px`,
                            }}
                        >
                                            {String(beatIndex + 1).padStart(2, "0")} · {beat.speaker ?? "서사"}
                                        </div>
                                        {beatIndex === index && (
                                            <div
                                                className="rounded-pill px-s py-[2px] font-display"
                                                style={{
                                                    color: tokens.color.parchment,
                                                    background: `${variantMeta.accent}16`,
                                                    border: `1px solid ${variantMeta.border}`,
                                                    fontSize: 12.5,
                                                    letterSpacing: tokens.type.letterSpacing.wide,
                                                    whiteSpace: "nowrap",
                                                    textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                                }}
                                            >
                                                현재 장면
                                            </div>
                                        )}
                                    </div>
                                    <div
                                        className="mt-[4px] font-body"
                                        style={{
                                            color: tokens.color.parchment,
                                            fontSize: tokens.type.size.small,
                                            lineHeight: tokens.type.lineHeight.normal,
                                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                        }}
                                    >
                                        {beat.text}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            <div
                className="mt-m rounded-m px-m py-s"
                style={{
                    border: `1px solid ${variantMeta.border}`,
                    background: `linear-gradient(180deg, rgba(14,12,24,0.82), rgba(14,12,24,0.58))`,
                    boxShadow: `0 12px 28px ${tokens.color.ink}66`,
                }}
            >
                <div
                    className="mb-s flex items-center gap-xs"
                    aria-hidden="true"
                >
                    {beats.map((beat, beatIndex) => {
                        const active = beatIndex === index;
                        const passed = beatIndex < index;
                        const dotColor = active
                            ? variantMeta.accent
                            : passed
                                ? `${variantMeta.accent}88`
                                : `${tokens.color.parchmentDim}33`;
                        return (
                            <motion.div
                                key={`${beat.type}-${beatIndex}`}
                                animate={{
                                    scale: active ? 1.05 : 1,
                                    opacity: active ? 1 : passed ? 0.8 : 0.45,
                                }}
                                transition={{ duration: 0.22 }}
                                style={{
                                    width: active ? 28 : 12,
                                    height: 8,
                                    borderRadius: 999,
                                    background: dotColor,
                                    boxShadow: active ? `0 0 18px ${variantMeta.glow}` : "none",
                                }}
                            />
                        );
                    })}
                </div>
                <div
                    className="flex items-center justify-between"
                    style={{ color: progressAccent, fontSize: tokens.type.size.small, letterSpacing: tokens.type.letterSpacing.wide }}
                >
                    <span className="font-stat">{index + 1} / {beats.length}</span>
                    <div className="flex items-center gap-m">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                goPrev();
                            }}
                            disabled={index === 0}
                            className="rounded-pill px-s py-[2px] font-body"
                            style={{
                                color: index === 0 ? `${tokens.color.parchment}88` : tokens.color.parchment,
                                background: index === 0 ? `${tokens.color.ink}66` : `${tokens.color.ink}99`,
                                border: `1px solid ${index === 0 ? `${tokens.color.parchmentDim}22` : `${tokens.color.parchmentDim}3A`}`,
                                fontSize: tokens.type.size.small,
                                letterSpacing: tokens.type.letterSpacing.wide,
                                cursor: index === 0 ? "default" : "pointer",
                                opacity: index === 0 ? 0.65 : 1,
                                textShadow: `${tokens.color.ink} 0px 2px 8px`,
                            }}
                            title="바로 이전 장면으로 되돌아갑니다"
                        >
                            이전 장면
                        </button>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setHistoryOpen((open) => !open);
                            }}
                            className="rounded-pill px-s py-[2px] font-body"
                            style={{
                                color: tokens.color.parchment,
                                background: historyOpen ? `${variantMeta.accent}14` : `${tokens.color.ink}99`,
                                border: `1px solid ${historyOpen ? variantMeta.border : `${tokens.color.parchmentDim}3A`}`,
                                fontSize: tokens.type.size.small,
                                letterSpacing: tokens.type.letterSpacing.wide,
                                cursor: "pointer",
                                textShadow: `${tokens.color.ink} 0px 2px 8px`,
                            }}
                            title="지금까지 읽은 장면 기록을 펼치거나 접기"
                        >
                            {historyOpen ? "기록 접기" : "장면 기록 펼치기"}
                        </button>
                        {current.audioSrc && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    replayNarration();
                                }}
                                className="rounded-pill px-s py-[2px] font-body"
                                style={{
                                    color: narrationBlocked ? tokens.color.ember : tokens.color.parchment,
                                    background: narrationBlocked ? `${tokens.color.ember}16` : `${tokens.color.ink}99`,
                                    border: `1px solid ${narrationBlocked ? `${tokens.color.ember}55` : `${tokens.color.parchmentDim}3A`}`,
                                    fontSize: tokens.type.size.small,
                                    letterSpacing: tokens.type.letterSpacing.wide,
                                    cursor: "pointer",
                                    textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                }}
                                title="현재 장면 나레이션을 다시 재생합니다"
                            >
                                {narrationBlocked ? "나레이션 재생" : "나레이션 다시 듣기"}
                            </button>
                        )}
                        {/*
                         * 전체 건너뛰기 — 긴 컷신(원정 서막 등 10+ beat) 에서 반복 플레이 시 사용.
                         * 첫 beat 에서는 비활성(실수 방지), 2번째 beat 부터 노출. 마지막 beat 에서는 숨김 (이미 끝).
                         */}
                        {index >= 1 && index < beats.length - 1 && (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); skipAll(); }}
                                className="rounded-pill px-s py-[2px] font-body"
                                style={{
                                    color: tokens.color.parchment,
                                    background: `${tokens.color.ink}99`,
                                    border: `1px solid ${tokens.color.parchmentDim}3A`,
                                    fontSize: tokens.type.size.small,
                                    letterSpacing: tokens.type.letterSpacing.wide,
                                    cursor: "pointer",
                                    textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                }}
                                title="이 시퀀스 전체를 건너뛰고 결과 화면으로"
                            >
                                전체 건너뛰기
                            </button>
                        )}
                        {beatDone ? (
                            <motion.button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    goNext();
                                }}
                                animate={{ boxShadow: [
                                    `0 0 14px ${variantMeta.glow}`,
                                    `0 0 26px ${variantMeta.accent}AA, 0 0 6px #FFF6D866`,
                                    `0 0 14px ${variantMeta.glow}`,
                                ] }}
                                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                                className="rounded-pill px-m py-[4px] font-body"
                                style={{
                                    color: tokens.color.ink,
                                    background: variantMeta.accent,
                                    border: `1px solid ${variantMeta.border}`,
                                    fontSize: tokens.type.size.small,
                                    fontWeight: 700,
                                    letterSpacing: tokens.type.letterSpacing.wide,
                                    cursor: "pointer",
                                }}
                                title="다음 장면으로 넘깁니다"
                            >
                                다음 장면 ▸
                            </motion.button>
                        ) : (
                            <>
                                <span style={{ opacity: 0.72 }}>
                                    장면 넘기기
                                </span>
                                <motion.div
                                    animate={{ opacity: 0.36 }}
                                    transition={{ duration: 0.2 }}
                                    className="font-display"
                                    style={{
                                        color: `${variantMeta.accent}66`,
                                        fontSize: tokens.type.size.small,
                                    }}
                                >
                                    ▸
                                </motion.div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function resolveBeatVariantMeta(
    variant: NarrativeBeat["variant"],
    theme: NarrativeBeatPlayerProps["theme"],
) {
    if (variant === "briefing") {
        return {
            label: "장 브리핑",
            copy: "지형과 압박 축, 이번 장의 진영 분위기를 함께 보여 주는 장면입니다.",
            accent: tokens.color.steel,
            border: `${tokens.color.steel}77`,
            glow: "rgba(113,128,150,0.22)",
            bannerBackground: "linear-gradient(90deg, rgba(74,85,104,0.24), rgba(14,12,24,0.7))",
            panelBackground: "linear-gradient(180deg, rgba(22,28,38,0.92), rgba(14,12,24,0.94))",
        };
    }
    if (variant === "memorial") {
        return {
            label: "기억 컷신",
            copy: "희생과 이름, 남겨진 편지를 천천히 회수하는 장면입니다.",
            accent: tokens.color.burgundy,
            border: `${tokens.color.burgundy}88`,
            glow: "rgba(92,31,46,0.24)",
            bannerBackground: "linear-gradient(90deg, rgba(92,31,46,0.3), rgba(14,12,24,0.78))",
            panelBackground: "linear-gradient(180deg, rgba(26,16,24,0.94), rgba(14,12,24,0.96))",
        };
    }
    if (variant === "decision") {
        return {
            label: "결단 컷신",
            copy: "앞선 누적 선택이 마지막 판단의 무게로 되돌아오는 장면입니다.",
            accent: tokens.color.ember,
            border: `${tokens.color.ember}77`,
            glow: "rgba(201,106,59,0.24)",
            bannerBackground: "linear-gradient(90deg, rgba(201,106,59,0.22), rgba(14,12,24,0.72))",
            panelBackground: "linear-gradient(180deg, rgba(30,20,18,0.94), rgba(14,12,24,0.95))",
        };
    }
    return {
        label: theme === "soul" ? "영혼의 잔향" : "기록된 장면",
        copy: "기본 서사 레이아웃입니다.",
        accent: theme === "soul" ? tokens.color.ember : tokens.color.parchmentDim,
        border: theme === "soul" ? `${tokens.color.burgundy}88` : `${tokens.color.parchmentDim}44`,
        glow: theme === "soul" ? "rgba(92,31,46,0.22)" : "rgba(184,169,132,0.12)",
        bannerBackground: theme === "soul"
            ? "linear-gradient(90deg, rgba(92,31,46,0.28), rgba(14,12,24,0.72))"
            : "linear-gradient(90deg, rgba(184,169,132,0.12), rgba(14,12,24,0.68))",
        panelBackground: theme === "soul"
            ? "linear-gradient(180deg, rgba(92,31,46,0.18), rgba(14,12,24,0.94))"
            : `${tokens.color.ink}E6`,
    };
}

interface AmbientParticle {
    top: string;
    left: string;
    size: number;
    duration: number;
    delay: number;
}

interface CutsceneAmbientProfile {
    overlay: string;
    veil: string;
    particles: AmbientParticle[];
    driftX: number[];
    driftY: number[];
    driftScale: number[];
    driftDuration: number;
    imageFilter: string;
    imageOpacity: number;
    impactTint: string;
    accentGlow: string;
}

interface NarrativeIllustrationStageProps {
    src: string;
    alt: string;
    onError: () => void;
    fit: "cover" | "contain";
    position: string;
    profile: CutsceneAmbientProfile;
    impactNonce: number;
    minHeight?: number;
    maxHeight?: number;
}

const DEFAULT_AMBIENT_PARTICLES: AmbientParticle[] = [
    { top: "12%", left: "16%", size: 7, duration: 15, delay: 0.2 },
    { top: "24%", left: "71%", size: 5, duration: 18, delay: 0.8 },
    { top: "36%", left: "48%", size: 6, duration: 16, delay: 1.2 },
    { top: "54%", left: "27%", size: 8, duration: 19, delay: 0.5 },
    { top: "64%", left: "79%", size: 6, duration: 17, delay: 1.6 },
    { top: "74%", left: "43%", size: 5, duration: 14, delay: 0.1 },
    { top: "82%", left: "14%", size: 4, duration: 20, delay: 1.1 },
    { top: "18%", left: "88%", size: 4, duration: 13, delay: 0.6 },
];

function resolveCutsceneAmbientProfile(
    variant: NarrativeBeat["variant"],
    theme: NarrativeBeatPlayerProps["theme"],
    mood?: string | null,
): CutsceneAmbientProfile {
    const normalizedMood = mood?.toLowerCase() ?? "";
    const soulTint = theme === "soul";

    if (variant === "decision") {
        return {
            overlay: "linear-gradient(180deg, rgba(17,12,15,0.16), rgba(201,106,59,0.12) 58%, rgba(14,12,24,0.58))",
            veil: "radial-gradient(circle at 50% 58%, rgba(201,106,59,0.22), rgba(201,106,59,0) 54%)",
            particles: DEFAULT_AMBIENT_PARTICLES,
            driftX: [-10, 12, -6],
            driftY: [4, -8, 3],
            driftScale: [1.02, 1.05, 1.025],
            driftDuration: 26,
            imageFilter: "saturate(0.98) contrast(1.04)",
            imageOpacity: 0.96,
            impactTint: "rgba(201,106,59,0.28)",
            accentGlow: "rgba(255, 228, 184, 0.18)",
        };
    }

    if (variant === "memorial") {
        return {
            overlay: "linear-gradient(180deg, rgba(14,12,24,0.12), rgba(92,31,46,0.18) 56%, rgba(14,12,24,0.64))",
            veil: "radial-gradient(circle at 50% 62%, rgba(92,31,46,0.26), rgba(92,31,46,0) 56%)",
            particles: DEFAULT_AMBIENT_PARTICLES.slice(0, 6),
            driftX: [-4, 5, -3],
            driftY: [2, -4, 2],
            driftScale: [1.01, 1.03, 1.015],
            driftDuration: 32,
            imageFilter: "saturate(0.82) contrast(1.03) brightness(0.92)",
            imageOpacity: 0.92,
            impactTint: "rgba(92,31,46,0.24)",
            accentGlow: "rgba(255, 226, 214, 0.14)",
        };
    }

    if (variant === "briefing") {
        return {
            overlay: "linear-gradient(180deg, rgba(18,24,34,0.14), rgba(52,74,99,0.12) 46%, rgba(14,12,24,0.54))",
            veil: "radial-gradient(circle at 46% 30%, rgba(74,85,104,0.18), rgba(74,85,104,0) 48%)",
            particles: DEFAULT_AMBIENT_PARTICLES,
            driftX: [-12, 9, -8],
            driftY: [3, -7, 2],
            driftScale: [1.03, 1.06, 1.04],
            driftDuration: 28,
            imageFilter: "saturate(0.94) contrast(1.05)",
            imageOpacity: 0.97,
            impactTint: "rgba(113,128,150,0.18)",
            accentGlow: "rgba(208, 219, 235, 0.16)",
        };
    }

    if (normalizedMood.includes("grim") || normalizedMood.includes("stern") || normalizedMood.includes("sealed")) {
        return {
            overlay: "linear-gradient(180deg, rgba(14,12,24,0.14), rgba(92,31,46,0.14) 44%, rgba(14,12,24,0.58))",
            veil: "radial-gradient(circle at 50% 56%, rgba(92,31,46,0.16), rgba(92,31,46,0) 52%)",
            particles: DEFAULT_AMBIENT_PARTICLES.slice(1),
            driftX: [-8, 8, -5],
            driftY: [2, -5, 3],
            driftScale: [1.02, 1.045, 1.025],
            driftDuration: 30,
            imageFilter: "saturate(0.88) contrast(1.05)",
            imageOpacity: 0.95,
            impactTint: "rgba(92,31,46,0.18)",
            accentGlow: "rgba(255, 233, 230, 0.12)",
        };
    }

    return {
        overlay: soulTint
            ? "linear-gradient(180deg, rgba(20,14,18,0.12), rgba(92,31,46,0.12) 46%, rgba(14,12,24,0.54))"
            : "linear-gradient(180deg, rgba(16,14,20,0.08), rgba(184,169,132,0.08) 44%, rgba(14,12,24,0.48))",
        veil: soulTint
            ? "radial-gradient(circle at 50% 42%, rgba(92,31,46,0.16), rgba(92,31,46,0) 48%)"
            : "radial-gradient(circle at 50% 38%, rgba(184,169,132,0.14), rgba(184,169,132,0) 46%)",
        particles: DEFAULT_AMBIENT_PARTICLES,
        driftX: [-9, 7, -5],
        driftY: [3, -6, 2],
        driftScale: [1.025, 1.05, 1.03],
        driftDuration: 29,
        imageFilter: soulTint ? "saturate(0.92) contrast(1.04)" : "saturate(0.98) contrast(1.03)",
        imageOpacity: 0.96,
        impactTint: soulTint ? "rgba(92,31,46,0.18)" : "rgba(184,169,132,0.14)",
        accentGlow: soulTint ? "rgba(255, 222, 214, 0.12)" : "rgba(246, 237, 209, 0.12)",
    };
}

function resolveNarrativeAccentCue(
    variant: NarrativeBeat["variant"],
    mood?: string | null,
): { src: string; volume: number; delayMs: number } | null {
    const normalizedMood = mood?.toLowerCase() ?? "";

    if (variant === "briefing") {
        return { src: tokens.audio.sfx.bookOpen, volume: 0.22, delayMs: 70 };
    }
    if (variant === "decision") {
        return { src: tokens.audio.sfx.cardFlip, volume: 0.26, delayMs: 60 };
    }
    if (variant === "memorial") {
        return { src: tokens.audio.sfx.whisper, volume: 0.18, delayMs: 120 };
    }
    if (normalizedMood.includes("sealed") || normalizedMood.includes("binding")) {
        return { src: tokens.audio.sfx.inkBloom, volume: 0.2, delayMs: 90 };
    }
    if (normalizedMood.includes("grim") || normalizedMood.includes("stern") || normalizedMood.includes("reverent")) {
        return { src: tokens.audio.sfx.whisper, volume: 0.14, delayMs: 110 };
    }
    if (normalizedMood.includes("radiant") || normalizedMood.includes("triumphant")) {
        return { src: tokens.audio.sfx.successShimmer, volume: 0.14, delayMs: 120 };
    }
    if (normalizedMood.includes("thread") || normalizedMood.includes("woven") || normalizedMood.includes("fate")) {
        return { src: tokens.audio.sfx.threadWeave, volume: 0.18, delayMs: 90 };
    }
    return null;
}

function NarrativeIllustrationStage({
    src,
    alt,
    onError,
    fit,
    position,
    profile,
    impactNonce,
    minHeight,
    maxHeight,
}: NarrativeIllustrationStageProps) {
    return (
        <div
            className="relative isolate w-full overflow-hidden"
            style={{
                minHeight,
                maxHeight,
                background: "linear-gradient(180deg, rgba(14,12,24,0.22), rgba(14,12,24,0.54))",
            }}
        >
            <motion.img
                src={src}
                alt={alt}
                onError={onError}
                className="block h-full w-full"
                animate={{
                    x: profile.driftX,
                    y: profile.driftY,
                    scale: profile.driftScale,
                }}
                transition={{
                    duration: profile.driftDuration,
                    repeat: Infinity,
                    repeatType: "mirror",
                    ease: "easeInOut",
                }}
                style={{
                    maxHeight,
                    minHeight,
                    objectFit: fit,
                    objectPosition: position,
                    opacity: profile.imageOpacity,
                    filter: profile.imageFilter,
                    transformOrigin: "center center",
                }}
            />
            <div
                className="pointer-events-none absolute inset-0"
                style={{
                    background: profile.overlay,
                }}
            />
            <motion.div
                className="pointer-events-none absolute inset-0"
                animate={{ opacity: [0.34, 0.52, 0.34] }}
                transition={{ duration: 8.6, repeat: Infinity, ease: "easeInOut" }}
                style={{
                    background: profile.veil,
                    mixBlendMode: "screen",
                }}
            />
            {profile.particles.map((particle, particleIndex) => (
                <motion.span
                    key={`${particle.left}:${particle.top}:${particleIndex}`}
                    className="pointer-events-none absolute rounded-full"
                    animate={{
                        opacity: [0.12, 0.34, 0.1],
                        y: [0, -10, -2],
                        x: [0, 3, -2],
                        scale: [1, 1.18, 0.94],
                    }}
                    transition={{
                        duration: particle.duration,
                        delay: particle.delay,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                    style={{
                        top: particle.top,
                        left: particle.left,
                        width: particle.size,
                        height: particle.size,
                        background: profile.accentGlow,
                        boxShadow: `0 0 12px ${profile.accentGlow}`,
                    }}
                />
            ))}
            <AnimatePresence mode="wait">
                <motion.div
                    key={`impact-${impactNonce}`}
                    className="pointer-events-none absolute inset-0"
                    initial={{ opacity: 0.26, scale: 1.02 }}
                    animate={{ opacity: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.55, ease: "easeOut" }}
                    style={{
                        background: `radial-gradient(circle at 50% 50%, ${profile.impactTint}, transparent 62%)`,
                        mixBlendMode: "screen",
                    }}
                />
            </AnimatePresence>
        </div>
    );
}

/**
 * decision variant 의 이미지 슬롯 placeholder. 아직 컷신 일러스트가 없을 때 2-column
 * 레이아웃이 비어 보이지 않도록 중앙 ember 사선 장식 + 배경 글로우만 렌더.
 * 이미지가 주입되면 이 컴포넌트 자리는 사라짐.
 */
function DecisionFallbackOrnament({
    accent,
    border,
    profile,
    impactNonce,
}: {
    accent: string;
    border: string;
    profile: CutsceneAmbientProfile;
    impactNonce: number;
}) {
    return (
        <div
            className="relative flex h-full w-full items-center justify-center"
            style={{
                background: `radial-gradient(circle at 50% 55%, ${accent}22, transparent 72%)`,
            }}
            aria-hidden="true"
        >
            <div
                className="font-display"
                style={{
                    color: `${accent}CC`,
                    fontSize: 96,
                    lineHeight: 1,
                    letterSpacing: 18,
                    textShadow: `0 0 28px ${accent}66`,
                    opacity: 0.82,
                }}
            >
                ✦
            </div>
            <div
                className="absolute font-body"
                style={{
                    bottom: 18,
                    color: `${accent}99`,
                    fontSize: 12,
                    letterSpacing: 2,
                    textTransform: "uppercase",
                    opacity: 0.75,
                }}
            >
                Decision
            </div>
            <div
                className="absolute inset-x-m"
                style={{
                    top: 12,
                    height: 1,
                    background: `linear-gradient(90deg, transparent, ${border}, transparent)`,
                }}
            />
            <div
                className="absolute inset-x-m"
                style={{
                    bottom: 12,
                    height: 1,
                    background: `linear-gradient(90deg, transparent, ${border}, transparent)`,
                }}
            />
            <div
                className="pointer-events-none absolute inset-0"
                style={{
                    background: profile.overlay,
                }}
            />
            {profile.particles.slice(0, 5).map((particle, particleIndex) => (
                <motion.span
                    key={`decision-particle-${particleIndex}`}
                    className="pointer-events-none absolute rounded-full"
                    animate={{
                        opacity: [0.1, 0.26, 0.08],
                        y: [0, -8, -2],
                    }}
                    transition={{
                        duration: particle.duration,
                        delay: particle.delay,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                    style={{
                        top: particle.top,
                        left: particle.left,
                        width: particle.size,
                        height: particle.size,
                        background: profile.accentGlow,
                        boxShadow: `0 0 10px ${profile.accentGlow}`,
                    }}
                />
            ))}
            <AnimatePresence mode="wait">
                <motion.div
                    key={`decision-impact-${impactNonce}`}
                    className="pointer-events-none absolute inset-0"
                    initial={{ opacity: 0.22 }}
                    animate={{ opacity: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.48, ease: "easeOut" }}
                    style={{
                        background: `radial-gradient(circle at 50% 50%, ${profile.impactTint}, transparent 60%)`,
                        mixBlendMode: "screen",
                    }}
                />
            </AnimatePresence>
        </div>
    );
}

/**
 * memorial variant 의 이미지 슬롯 placeholder. burgundy 톤의 작은 "흔적" 장식 + 얇은
 * divider 로 추모 뉘앙스만 남김. 텍스트만 남은 경우에도 "밤의 무게" 가 약하게라도 유지.
 */
function MemorialFallbackOrnament({
    accent, border, panelBackground, glow, profile, impactNonce,
}: {
    accent: string;
    border: string;
    panelBackground: string;
    glow: string;
    profile: CutsceneAmbientProfile;
    impactNonce: number;
}) {
    return (
        <div
            className="mb-m rounded-l overflow-hidden"
            style={{
                border: `1px solid ${border}`,
                background: panelBackground,
                boxShadow: `0 14px 36px ${glow}`,
            }}
            aria-hidden="true"
        >
            <div
                className="relative flex items-center justify-center"
                style={{
                    minHeight: 180,
                    background: `radial-gradient(circle at 50% 58%, ${accent}28, transparent 68%)`,
                }}
            >
                <div
                    className="font-display"
                    style={{
                        color: `${accent}BB`,
                        fontSize: 68,
                        lineHeight: 1,
                        letterSpacing: 10,
                        textShadow: `0 0 24px ${accent}55`,
                        opacity: 0.82,
                    }}
                >
                    ✧
                </div>
                <div
                    className="absolute font-body"
                    style={{
                        bottom: 18,
                        color: `${accent}99`,
                        fontSize: 11,
                        letterSpacing: 4,
                        textTransform: "uppercase",
                        opacity: 0.7,
                    }}
                >
                    In Memoriam
                </div>
                <div
                    className="absolute inset-x-m"
                    style={{
                        top: 14,
                        height: 1,
                        background: `linear-gradient(90deg, transparent, ${border}, transparent)`,
                    }}
                />
                <div
                    className="absolute inset-x-m"
                    style={{
                        bottom: 14,
                        height: 1,
                        background: `linear-gradient(90deg, transparent, ${border}, transparent)`,
                    }}
                />
                <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                        background: profile.overlay,
                    }}
                />
                <motion.div
                    className="pointer-events-none absolute inset-0"
                    animate={{ opacity: [0.18, 0.34, 0.18] }}
                    transition={{ duration: 9.2, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                        background: profile.veil,
                        mixBlendMode: "screen",
                    }}
                />
                {profile.particles.slice(0, 5).map((particle, particleIndex) => (
                    <motion.span
                        key={`memorial-particle-${particleIndex}`}
                        className="pointer-events-none absolute rounded-full"
                        animate={{
                            opacity: [0.08, 0.22, 0.08],
                            y: [0, -9, -2],
                        }}
                        transition={{
                            duration: particle.duration + 2,
                            delay: particle.delay,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                        style={{
                            top: particle.top,
                            left: particle.left,
                            width: particle.size,
                            height: particle.size,
                            background: profile.accentGlow,
                            boxShadow: `0 0 10px ${profile.accentGlow}`,
                        }}
                    />
                ))}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={`memorial-impact-${impactNonce}`}
                        className="pointer-events-none absolute inset-0"
                        initial={{ opacity: 0.2 }}
                        animate={{ opacity: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.62, ease: "easeOut" }}
                        style={{
                            background: `radial-gradient(circle at 50% 52%, ${profile.impactTint}, transparent 60%)`,
                            mixBlendMode: "screen",
                        }}
                    />
                </AnimatePresence>
            </div>
        </div>
    );
}
